import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useV2GetPhaseByIdQuery } from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { PhaseForm } from "../PhaseForm/PhaseForm";
import { usePhaseMutations } from "../api/usePhaseMutations";
import type { PhaseFormValues } from "../types/Phase";

const FORM_ID = "phase-form";

export const Edit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useV2GetPhaseByIdQuery({ id: Number(id) });
  const { update } = usePhaseMutations();
  const [submitting, setSubmitting] = useState(false);
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<PhaseFormValues>();

  const initial = useMemo<PhaseFormValues | null>(() => {
    if (!data?.data) return null;
    const record: any = data.data;
    return {
      name: record.attributes.name ?? "",
      sowLanguage: record.attributes["sow-language"] ?? "",
      isDefault: !!record.attributes.default,
    };
  }, [data]);

  if (isLoading || !initial) return <ScopeStackSpinner />;

  const recordName = (data?.data as any)?.attributes?.name || "Phase";

  const handleSubmit = async (values: PhaseFormValues) => {
    setSubmitting(true);
    try {
      await update(id!, values);
      markSaved(values, "Phase updated.");
    } catch (err) {
      console.error("Failed to update phase", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title={`Edit ${recordName}`}
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        isSubmitting={submitting}
      >
        <PhaseForm
          key={formKey}
          id={FORM_ID}
          initialValues={savedInitial ?? initial}
          isPersisted={true}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
